import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Flag } from 'lucide-react';
import { CONTENT_SAVED_EVENT } from '../../lib/api';
import { flagTypeById, listFlags, type FlagListItem } from '../../lib/flags';
import { useFlagStore, useFlagStoreVersion } from '../../contexts/FlagStateContext';
import { FlagList } from './FlagList';

/**
 * The meeting's flags in its right panel (#316): open flags by type, plus
 * decisions, with addressed ones behind a toggle. Clicking one scrolls to
 * its line and opens it there. The header collapses it, like Follow-ups (#334).
 */
export function MeetingFlagsSection({ instanceId, onReveal }: { instanceId: string; onReveal: (flagId: string) => void }) {
  const store = useFlagStore();
  useFlagStoreVersion();
  const types = store.types();
  const [items, setItems] = useState<FlagListItem[] | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const load = useCallback(() => {
    listFlags({ types: [], status: 'all', sourceType: 'meeting_instance', sourceId: instanceId, limit: 200 })
      .then((r) => { store.seed(r.items); setItems(r.items); })
      .catch(() => setItems((prev) => prev ?? []));
  }, [instanceId, store]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onSaved = (e: Event) => {
      if ((e as CustomEvent<{ path: string }>).detail?.path === `/meeting-instances/${instanceId}`) load();
    };
    window.addEventListener(CONTENT_SAVED_EVENT, onSaved);
    return () => window.removeEventListener(CONTENT_SAVED_EVENT, onSaved);
  }, [instanceId, load]);

  // State changed through a popover shows here at once.
  const current = (items ?? []).map((i) => {
    const st = store.get(i.id);
    return st ? { ...i, status: st.status, reply: st.reply } : i;
  });
  const addressable = (i: FlagListItem) => flagTypeById(types, i.type).addressable;
  const open = current.filter((i) => addressable(i) && i.status === 'open').length;
  const shown = current.filter((i) => showDone || !addressable(i) || i.status === 'open');

  return (
    <div className="px-3 py-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="m-0 text-xs font-semibold uppercase tracking-wider text-text-secondary">
          <button type="button" aria-expanded={isOpen} onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-1.5 py-1 uppercase tracking-wider hover:text-text-primary transition-colors">
            {isOpen ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
            <Flag size={12} aria-hidden />Flags
            <span className="font-mono text-[11px] font-normal normal-case tracking-normal text-text-muted">{open} open</span>
          </button>
        </h3>
        {isOpen && (
          <label className="flex items-center gap-1.5 text-xs text-text-secondary">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
            Show addressed
          </label>
        )}
      </div>
      {isOpen && items !== null && (
        <FlagList items={shown} group="kind" showSource={false} types={types} onOpen={(i) => onReveal(i.id)}
          emptyText="No flags yet. Click a line and press Alt+1 to Alt+7." />
      )}
    </div>
  );
}
